/**
 * Releases ▸ **one release** (`/releases/10.2`) — the command center's
 * `ReleaseDetailPage` as a reseller sees it (2026-09-14).
 *
 * Everything on this page is about ONE release: its header, and the versions
 * shipped inside it. It is master–detail — a rail of versions down the left,
 * the selected version filling the right — because the reason to come here
 * is almost always "what is in the latest one?", and then "and the one
 * before it?", which a rail answers with one click.
 *
 * The newest version is selected on arrival. Which one is selected rides in
 * the URL (`?version=10.2.0.27`), so a refresh, the back button and a pasted
 * link all land on the same version. An unknown value falls back to the
 * newest; an unknown RELEASE in the path says so and offers the folders.
 *
 * The release is a container: the downloads, the change log and the
 * documentation all belong to the version on the right. There is no curation
 * anywhere on this page — a reseller downloads documents and disks, and that
 * is all.
 */
import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Folder } from "lucide-react";
import { useCallback, useEffect, useMemo, type JSX } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { BackButton } from "../../../components/ui/back-button.js";
import { PageLoader } from "../../../components/ui/page-loader.js";
import { DownloadRefused, type ReleaseRow } from "./api.js";
import { findFamily, versionsOf } from "./release-families.js";
import { RELEASES_PATH, VERSION_PARAM } from "./release-paths.js";
import { RELEASE_STATUS_COLOR, statusLabel } from "./release-status.js";
import { useReleases } from "./useReleases.js";
import { VersionDetail } from "./VersionDetail.js";
import { VersionRail } from "./VersionRail.js";

export function ReleaseDetailPage(): JSX.Element {
  const { release: releaseParam } = useParams<{ release: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { families, isLoading, isError, error, refetch } = useReleases();

  const family = findFamily(families, releaseParam);
  const versions = useMemo(() => (family ? versionsOf(family) : []), [family]);

  const selectVersion = useCallback(
    (version: string | null): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (version === null) next.delete(VERSION_PARAM);
          else next.set(VERSION_PARAM, version);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Which version is open: the URL's pick when it names one that exists,
  // else the newest. Never held in state — the URL is the state.
  const wanted = searchParams.get(VERSION_PARAM);
  const selected: ReleaseRow | undefined =
    versions.find((v) => v.version === wanted) ?? versions[0];

  // A `?version=` that names nothing is dropped rather than left lying about
  // what is shown.
  useEffect(() => {
    if (wanted && versions.length > 0 && !versions.some((v) => v.version === wanted)) {
      selectVersion(null);
    }
  }, [wanted, versions, selectVersion]);

  if (isLoading) return <PageLoader />;

  if (isError) {
    return (
      <Box>
        <BackButton to={RELEASES_PATH} label="All releases" size="small" />
        <Box sx={{ mt: 2, borderRadius: 1, border: 1, borderColor: "error.main", p: 2, fontSize: 14 }}>
          <Typography sx={{ mb: 1, color: "error.main" }}>
            Could not load releases
            {error instanceof DownloadRefused && error.message ? `: ${error.message}` : "."}
          </Typography>
          <Button variant="contained" size="small" onClick={() => void refetch()}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  if (!family) {
    return (
      <Box>
        <BackButton to={RELEASES_PATH} label="All releases" size="small" />
        <Typography sx={{ py: 6, textAlign: "center", fontSize: 14, color: "text.disabled" }}>
          There is no release {releaseParam ?? ""}. It may not have shipped yet, or the link may be
          wrong.
        </Typography>
      </Box>
    );
  }

  const { line, hasLine } = family;

  return (
    <Box data-testid={`release-page-${line.version}`}>
      <BackButton to={RELEASES_PATH} label="All releases" size="small" />

      {/* The header of ONE release: the number, and what is known about it
          without opening a version — the command center's `ReleaseHeader`
          without its New version / Edit / Publish / Delete controls. */}
      <Box
        data-testid={`release-header-${line.version}`}
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          px: 2.5,
          py: 2,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.02),
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 44,
            height: 44,
            borderRadius: 1,
            flexShrink: 0,
            color: "primary.main",
            bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
          }}
        >
          <Folder size={22} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              component="h3"
              sx={{
                fontFamily: (t) => t.typography.h6.fontFamily,
                fontSize: 24,
                fontWeight: 700,
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
              }}
            >
              Release {line.version}
            </Typography>
            <Chip
              size="small"
              variant={line.status === "released" ? "filled" : "outlined"}
              color={RELEASE_STATUS_COLOR[line.status] ?? "default"}
              label={statusLabel(line.status)}
              sx={{ height: 20 }}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${versions.length} version${versions.length === 1 ? "" : "s"}`}
              sx={{ height: 20 }}
            />
            {!hasLine && (
              <Tooltip title="This version's release is not on this page — it stands on its own.">
                <Chip size="small" variant="outlined" label="Unfiled" sx={{ height: 20 }} />
              </Tooltip>
            )}
          </Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
            {line.product_name ? `${line.product_name} · ` : ""}
            {line.description ?? "Every version shipped as part of this release."}
          </Typography>
        </Box>
      </Box>

      {versions.length === 0 ? (
        <Typography sx={{ py: 6, textAlign: "center", fontSize: 14, color: "text.disabled" }}>
          No versions in {line.version} have shipped yet.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 2.5,
            mt: 2.5,
            gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 260px) minmax(0, 1fr)" },
            alignItems: "start",
          }}
        >
          <VersionRail
            releaseVersion={line.version}
            versions={versions}
            selectedId={selected?.id}
            onSelect={(v) => selectVersion(v.version)}
          />
          {selected && <VersionDetail key={selected.id} release={selected} />}
        </Box>
      )}
    </Box>
  );
}

export default ReleaseDetailPage;
